import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import api from "@/lib/api";
import { Modal, PillBtn } from "./common";

// Admin: XP points per activity, milestone rewards, roles left off the boards, support email.
export default function XpRulesModal({ onClose, onSaved }) {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/growth/config").then((r) => setCfg(r.data)).catch(() => toast.error("Could not load settings")); }, []);
  if (!cfg) return null;
  const setRule = (k, v) => setCfg({ ...cfg, rules: { ...cfg.rules, [k]: v } });
  const setMs = (i, k, v) => setCfg({ ...cfg, milestones: cfg.milestones.map((m, j) => (j === i ? { ...m, [k]: v } : m)) });
  const save = async () => {
    try {
      await api.put("/growth/config", {
        rules: Object.fromEntries(Object.entries(cfg.rules).map(([k, v]) => [k, parseInt(v, 10) || 0])),
        milestones: cfg.milestones, support_email: cfg.support_email,
        excluded_roles: cfg.excluded_roles,
      });
      toast.success("Points rules saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  return (
    <Modal title="Points rules & milestones" onClose={onClose} testid="xp-rules-modal" wide>
      <h4 className="font-bold text-sm mb-2">XP per activity</h4>
      <div className="grid sm:grid-cols-2 gap-2 mb-5">
        {Object.entries(cfg.rules).map(([k, v]) => (
          <label key={k} className="flex items-center justify-between gap-2 border border-slate-100 rounded-xl px-3 py-2 text-sm">
            <span>{cfg.rule_labels[k]}</span>
            <input type="number" value={v} onChange={(e) => setRule(k, e.target.value)} className="w-20 text-right input-pill py-1" data-testid={`xp-rule-${k}`} />
          </label>
        ))}
      </div>
      <h4 className="font-bold text-sm mb-2">Automatic milestone rewards</h4>
      {cfg.milestones.map((m, i) => (
        <div key={i} className="flex gap-2 mb-2" data-testid={`xp-ms-${i}`}>
          <input type="number" value={m.xp} onChange={(e) => setMs(i, "xp", parseInt(e.target.value, 10) || 0)} className="input-pill w-24" placeholder="XP" />
          <input value={m.title} onChange={(e) => setMs(i, "title", e.target.value)} className="input-pill flex-1" placeholder="Reward title" />
          <input type="number" value={m.tokens} onChange={(e) => setMs(i, "tokens", parseInt(e.target.value, 10) || 0)} className="input-pill w-24" placeholder="Tokens" />
          <button onClick={() => setCfg({ ...cfg, milestones: cfg.milestones.filter((_, j) => j !== i) })} className="text-rose-500 p-2"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <button onClick={() => setCfg({ ...cfg, milestones: [...cfg.milestones, { xp: 0, title: "", tokens: 0 }] })} className="text-sm text-sky-600 font-semibold mb-5 inline-flex items-center gap-1" data-testid="xp-ms-add"><Plus className="w-4 h-4" /> Add milestone</button>
      <h4 className="font-bold text-sm mb-1">Roles left off the leaderboard (senior heads)</h4>
      <input value={cfg.excluded_roles.join(", ")} onChange={(e) => setCfg({ ...cfg, excluded_roles: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="input-pill w-full mb-5 text-sm" data-testid="xp-excluded" />
      <h4 className="font-bold text-sm mb-1">Support email (receives Learn Dashboard completion reports)</h4>
      <input value={cfg.support_email} onChange={(e) => setCfg({ ...cfg, support_email: e.target.value })} placeholder="Leave blank to use the app's sending email" className="input-pill w-full mb-5" data-testid="xp-support-email" />
      <PillBtn primary onClick={save} data-testid="xp-rules-save">Save rules</PillBtn>
    </Modal>
  );
}
